import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { notification } from 'antd';
import useImportCsv from '../../hooks/useImportCsv';
import { inHtmlTag } from '../../utils/inHtmlTag';
import { t } from '../../i18n/i18n';
import HorizontalLoader from '../HorizontalLoader/HorizontalLoader';
import ModalCustom from '../ModalCustom/ModalCustom';
import upload from '../../assets/sentScreening/upload.svg';
import './ScreeningImport.scss';
import { formatLimitDateToISO } from '../../utils/assessmentInvitation';

const ScreeningImport = ({ className, addRows, maxInvitationsBatch }) => {
  const { sendEmailFrom } = useSelector((state) => state.screenings);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [valueFile, setValueFile] = useState('');
  const { importRef, readFile, result } = useImportCsv(
    ['firstName', 'lastName', 'email', 'limitDate'],
    setLoading
  );

  useEffect(() => {
    if (result) {
      if (result.length > Number(process.env.RAZZLE_RUNTIME_MAX_SEARCH_EMAILS_BATCH_SIZE)) {
        notification.open({
          message: t('IMPORT_EXCEL_MAX_CANDIDATES_EXCEEDED'),
          type: 'error',
        });
        setLoading(false);
        return;
      }
      if (result?.length <= maxInvitationsBatch) {
        const resultFormatted = formatLimitDateToISO(result);
        addRows(resultFormatted);
      } else {
        notification.open({
          message: (
            <div
              dangerouslySetInnerHTML={{
                __html: t('IMPORT_EXCEL_NOT_ENOUGH_INVITATIONS_LEFT').replace(
                  '{invitationsLeft}',
                  inHtmlTag({
                    tag: 'b',
                    className: '',
                    content: maxInvitationsBatch,
                  })
                ),
              }}
            />
          ),
          type: 'error',
        });
      }
    }
    setLoading(false);
  }, [result]);

  const downloadTemplateCSV = () => {
    const headers = ['firstName', 'lastName', 'email', 'limitDate'];

    const rows = [
      [
        `"${'John'.replace(/"/g, '""')}"`,
        `"${'Doe'.replace(/"/g, '""')}"`,
        `"${'john.doe@example.com'.replace(/"/g, '""')}"`,
        `"${'28-12-31 23:59'.replace(/"/g, '""')}"`,
      ].join(','),
      [
        `"${'Jane'.replace(/"/g, '""')}"`,
        `"${'Smith'.replace(/"/g, '""')}"`,
        `"${'jane.smith@example.com'.replace(/"/g, '""')}"`,
        `"${'28-12-31 23:59'.replace(/"/g, '""')}"`,
      ].join(','),
    ];

    const csvContent = [headers.join(','), ...rows].join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `invite_candidates_${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    notification.success({
      message: t('CSV_EXPORTED_SUCCESS'),
      type: 'success',
    });
  };

  return (
    <div className={className}>
      <ModalCustom
        title={t('CREATE_SEARCH_ MODAL_SELECT_IMPORT')}
        onOkDisabled={!valueFile}
        show={showModal}
        setShow={setShowModal}
        onOkText={t('CREATE_SEARCH_MODAL_BUTTON_ADD')}
        onOk={() => {
          readFile();
          setShowModal(false);
          setValueFile('');
        }}
        onCancelText={
          <>
            <span
              className="bg-green-principal text-white"
              data-testid="export-invitations-csv-span"
            >
              {t('EXPORT_CSV')}
            </span>
          </>
        }
        noOkBtnClassname="bg-green-principal text-white border-none"
        onCancel={downloadTemplateCSV}
        classCross="text-base text-blue-principal"
        input={
          <>
            <div className="px-3 pb-2">
              <div
                style={{
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  borderRadius: '4px',
                  padding: '16px',
                }}
              >
                <p className="text-xs font-weight-600 mb-3" style={{ color: '#495057' }}>
                  {t('SCREENING_IMPORT_FORMAT_INFO')}
                </p>

                <div className="mb-3">
                  <p className="text-xs mb-1" style={{ color: '#6c757d' }}>
                    {t('SCREENING_IMPORT_FORMAT_REQUIRED')}:
                  </p>
                  <div
                    style={{
                      backgroundColor: 'white',
                      border: '1px solid #dee2e6',
                      borderRadius: '3px',
                      padding: '8px 12px',
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      color: '#495057',
                    }}
                  >
                    firstName,lastName,email,limitDate
                  </div>
                </div>

                <div className="mb-3">
                  <p className="text-xs mb-1" style={{ color: '#6c757d' }}>
                    {t('SCREENING_IMPORT_FORMAT_EXAMPLE')}:
                  </p>
                  <div
                    style={{
                      backgroundColor: 'white',
                      border: '1px solid #dee2e6',
                      borderRadius: '3px',
                      padding: '8px 12px',
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      lineHeight: '1.5',
                      color: '#495057',
                    }}
                  >
                    John,Doe,john.doe@example.com,24-12-31 23:59
                    <br />
                    Jane,Smith,jane.smith@example.com,24-12-31 23:59
                  </div>
                </div>
                <div
                  style={{
                    borderTop: '1px solid #dee2e6',
                    paddingTop: '10px',
                    marginTop: '10px',
                  }}
                >
                  <p className="text-xs mb-0" style={{ color: '#6c757d' }}>
                    <span style={{ color: '#495057', fontWeight: '500' }}>
                      {t('SCREENING_IMPORT_FORMAT_NOTE')}:
                    </span>{' '}
                    {t('SCREENING_IMPORT_FORMAT_DATE_DESC')}
                  </p>
                </div>
              </div>
            </div>
            <div className="outline-none custom-file-button ml-2 mt-3 py-2 px-4 font-weight-600 text-xs mx-auto w-75">
              <input
                type="file"
                name="file"
                id="fileEmails"
                hidden
                ref={importRef}
                onChange={(e) => setValueFile(e.target.value.slice(12))}
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              />
              {valueFile && (
                <p className="my-0 float-left font-weight-600 text-xs">
                  {`${valueFile.slice(0, 30)}...`}
                </p>
              )}
              <label htmlFor="fileEmails" className="float-right">
                <img src={upload} alt={t('CREATE_SEARCH_UPLOAD_ICON')} />
              </label>
            </div>
          </>
        }
      />
      {loading && <HorizontalLoader />}
      <h5 className="text-base  font-weight-600 mt-2">{t('SCREENING_IMPORT_TITLE')}</h5>
      <p
        className=" font-weight-500 text-xs mt-2 gray-subtitle"
        dangerouslySetInnerHTML={{
          __html: t('SCREENING_IMPORT_TEXT')
            .replace(
              '{firstBold}',
              inHtmlTag({
                tag: 'b',
                content: t('SCREENING_IMPORT_TEXT_FIRST_BOLD'),
                className: '',
              })
            )
            .replace(
              '{secondBold}',
              inHtmlTag({
                tag: 'b',
                content: t('SCREENING_IMPORT_TEXT_SECOND_BOLD'),
                className: '',
              })
            )
            .replace(
              '{thirdBold}',
              inHtmlTag({
                tag: 'b',
                content: t('SCREENING_IMPORT_TEXT_THIRD_BOLD'),
                className: '',
              })
            )
            .replace(
              '{fourthBold}',
              inHtmlTag({
                tag: 'b',
                content: t('SCREENING_IMPORT_TEXT_FOURTH_BOLD'),
                className: '',
              })
            ),
        }}
      />
      <button
        className="outline-none border-0 bg-transparent"
        type="button"
        data-testid="button-import-excel"
        onClick={() => {
          setShowModal(true);
        }}
      >
        <span className="text-blue-principal text-xs  font-weight-600">
          {t('SCREENING_IMPORT_BUTTON')}
        </span>
      </button>
    </div>
  );
};

export default ScreeningImport;
